import { sha256Hex } from './sha256Hex'

/** One signed entry: its id and the SVG markup it was tessellated from. */
export interface SignedPath {
  readonly id: string
  readonly path: string
}

export type SignatureTheme = 'light' | 'dark'

/** The writer's theme backgrounds as 0–255 channels (`metro-server` `geometry.LightBG` / `DarkBG`). */
const BACKGROUND: Record<SignatureTheme, readonly [number, number, number]> = {
  light: [255, 255, 255],
  dark: [28, 28, 28],
}

/**
 * The MMTG per-type signature (MMTG_FORMAT.md "Signature computation"),
 * mirroring Go `mmtg.ComputeSignature` and iOS `MetroMapGeometrySignature`:
 * SHA-256 over the (id, markup) pairs sorted by id then markup, each as
 * `id 0x1E path 0x1F`, then the theme byte, the background RGB and the
 * big-endian type version; the first 8 digest bytes as a little-endian u64.
 */
export async function geometrySignature(
  pairs: readonly SignedPath[],
  theme: SignatureTheme,
  version: number,
): Promise<bigint> {
  const sorted = [...pairs].sort((a, b) =>
    a.id !== b.id ? (a.id < b.id ? -1 : 1) : a.path < b.path ? -1 : a.path > b.path ? 1 : 0,
  )
  const encoder = new TextEncoder()
  const chunks: Uint8Array[] = []
  let length = 0
  for (const p of sorted) {
    for (const chunk of [
      encoder.encode(p.id),
      Uint8Array.of(0x1e),
      encoder.encode(p.path),
      Uint8Array.of(0x1f),
    ]) {
      chunks.push(chunk)
      length += chunk.length
    }
  }
  const tail = new Uint8Array(8)
  tail[0] = theme === 'dark' ? 1 : 0
  tail.set(BACKGROUND[theme], 1)
  new DataView(tail.buffer).setUint32(4, version, false)
  chunks.push(tail)
  length += tail.length

  const data = new Uint8Array(length)
  let offset = 0
  for (const chunk of chunks) {
    data.set(chunk, offset)
    offset += chunk.length
  }
  const hex = await sha256Hex(data)
  let result = 0n
  for (let i = 0; i < 8; i++) {
    result |= BigInt(parseInt(hex.slice(i * 2, i * 2 + 2), 16)) << BigInt(8 * i)
  }
  return result
}
